import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  ListSubheader,
  MenuItem,
  Select,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HpoReleaseNote from './HpoReleaseNote';
import {
  memberKey,
  memberLabel,
  relationHeading,
  relationText,
  subjectLabelText
} from './clinicalSuggestions/relativeMatch';
import { PRENATAL_GENOTYPE_SKIPPED_TEXT } from './clinicalSuggestions/acceptOutcome';
import {
  GENOTYPES_NOT_OFFERED_TEXT,
  REPORT_PEOPLE_GENOTYPES_NOT_OFFERED_TEXT
} from './clinicalSuggestions/prenatalSuggestions';
import { TILE_COLOURS } from 'src/components/pedigree-chart/pedigree-sidebar/sections/tileColours';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';

// Suggested Clinical Terms, grouped by PERSON (TGCLOUD-1845, decision 6).
//
// Each finding shows how the document put it (a status badge), the words it
// came from, and — for a relative — who in the pedigree it will be added to.
// Only definite findings start ticked; an uncertain, second-hand or suspected
// one, or a variant of uncertain significance, is shown but left for the
// clinician to tick.

// Each kind wears the register its records wear everywhere else: the sidebar's
// record tiles and the legend drag chips (sections/tileColours.js). The
// genotype pill used to be #2e7d32, the "clinically confirmed" green, beside a
// green Confirmed badge.
export const KIND = {
  phenotype: { label: 'Phenotype', register: TILE_COLOURS.phenotype },
  disease: { label: 'Disease', register: TILE_COLOURS.disease },
  genotype: { label: 'Genotype', register: TILE_COLOURS.genotype }
};

const STATUS = {
  absent: {
    label: 'Absent',
    color: 'error',
    hint: 'The document says this is NOT present. Added as an excluded term.'
  },
  uncertain: {
    label: 'Uncertain',
    color: 'warning',
    hint: 'The document hedges this. Added with a note saying so.'
  },
  suspected: {
    label: 'Suspected',
    color: 'warning',
    hint: 'Under consideration, not confirmed. Added with a note saying so.'
  },
  reported: {
    label: 'Second-hand',
    color: 'secondary',
    hint: 'Reported by someone else, not observed. Added with a note saying so.'
  },
  past: { label: 'Past', color: 'default', hint: 'Resolved or in the past.' },
  confirmed: {
    label: 'Confirmed',
    color: 'success',
    hint: 'The document confirms this diagnosis.'
  },
  // TGCLOUD-1866 D2. A condition linked to the gene of a variant reported
  // here, which the document does not say this person has. It used to be
  // shown as Confirmed, and ticked.
  associated: {
    label: 'Gene association',
    color: 'default',
    hint: 'Linked to the gene of a variant reported here, but the document does not say this person has it. Add it only if they do.'
  }
};

const ageText = (age, t) =>
  age === null || age === undefined || age === ''
    ? null
    : t('age {{age}}', { age });

// The words a document's variant is described with: a classification is one
// of the extractor's five, a zygosity one of the words it reads. Translated
// when they are; anything else is shown as the document gave it.
const CLASSIFICATIONS = new Set([
  'Pathogenic',
  'Likely pathogenic',
  'Uncertain significance',
  'Likely benign',
  'Benign'
]);
const ZYGOSITIES = new Set([
  'compound heterozygous',
  'heterozygous',
  'homozygous',
  'hemizygous',
  'heterozygote',
  'homozygote',
  'hemizygote',
  'mosaic'
]);
const vocabularyText = (known, value, t) =>
  known.has(value) ? t(value) : value;

function FindingRow({ subjectKey, finding: f, checked, disabled, onToggle }) {
  const t = useAutoTranslation();

  const kind = KIND[f.kind] || KIND.phenotype;
  const status = STATUS[f.status];
  const vus =
    f.kind === 'genotype' && f.classification === 'Uncertain significance';
  return (
    <Box
      data-testid={`finding-${subjectKey}-${f.id}`}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5 }}
    >
      <Checkbox
        size="small"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        inputProps={{ 'aria-label': t('Add {{name}}', { name: f.name }) }}
        sx={{ p: 0.25, mt: -0.25 }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {f.name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {f.id}
          </Typography>
          <Chip
            label={t(kind.label)}
            size="small"
            variant="outlined"
            data-testid={`kind-${f.kind}`}
            sx={{
              height: 18,
              fontSize: '0.68rem',
              backgroundColor: kind.register.background,
              borderColor: kind.register.border,
              color: kind.register.text
            }}
          />
          {status && (
            <Tooltip title={t(status.hint)} arrow>
              <Chip
                label={t(status.label)}
                size="small"
                color={status.color}
                sx={{ height: 18, fontSize: '0.68rem' }}
              />
            </Tooltip>
          )}
          {ageText(f.age, t) && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {ageText(f.age, t)}
            </Typography>
          )}
          {f.source === 'ai' && (
            <Tooltip
              title={t(
                'Found by AI, not by the vocabulary match. The quote is from the document; check the term before adding it.'
              )}
              arrow
            >
              <Chip
                label={t('AI')}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.68rem' }}
              />
            </Tooltip>
          )}
          {f.alreadyRecorded && (
            <Chip
              label={t('Already recorded')}
              size="small"
              sx={{ height: 18, fontSize: '0.68rem' }}
            />
          )}
        </Box>
        {f.kind === 'genotype' && f.variant && (
          <Typography
            variant="caption"
            component="div"
            sx={{ color: 'text.secondary' }}
          >
            {[
              f.variant.transcript && f.variant.hgvsC
                ? `${f.variant.transcript}:${f.variant.hgvsC}`
                : f.variant.hgvsC,
              f.variant.hgvsP,
              f.variant.zygosity &&
                vocabularyText(ZYGOSITIES, f.variant.zygosity, t)
            ]
              .filter(Boolean)
              .join(' · ')}
            {f.classification
              ? ` · ${vocabularyText(CLASSIFICATIONS, f.classification, t)}`
              : ''}
            {vus ? ` — ${t('not for predictive testing or diagnosis')}` : ''}
            {f.notSighted ? ` · ${t('report not sighted')}` : ''}
          </Typography>
        )}
        {f.kind === 'disease' && (f.support?.gene || f.support?.phenotypes) ? (
          <Typography
            variant="caption"
            component="div"
            sx={{ color: 'text.secondary' }}
          >
            {[
              f.support.gene
                ? t('linked to the {{gene}} variant reported', {
                    gene: f.support.gene
                  })
                : null,
              f.support.phenotypes
                ? f.support.phenotypes === 1
                  ? t('1 phenotype here matches its HPO annotations')
                  : t('{{count}} phenotypes here match its HPO annotations', {
                      count: f.support.phenotypes
                    })
                : null
            ]
              .filter(Boolean)
              .join('; ')}
          </Typography>
        ) : null}
        {/* TGCLOUD-1866, the review of D2: the document states this as a
            diagnosis AND calls this person a carrier. Neither reading is
            taken over the other; both statements are shown. */}
        {f.conflict === 'carrier' && (
          <Typography
            variant="caption"
            component="div"
            data-testid="carrier-conflict"
            sx={{ color: 'warning.dark' }}
          >
            {t(
              'The document gives this both as a diagnosis and as carrier status for this person. Check which is right before adding it.'
            )}
          </Typography>
        )}
        {(f.evidence || [])
          .slice(0, f.conflict ? 2 : 1)
          .filter(Boolean)
          .map((quote) => (
            <Typography
              key={quote}
              variant="caption"
              component="div"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
                overflowWrap: 'anywhere'
              }}
            >
              “{quote}”
            </Typography>
          ))}
      </Box>
    </Box>
  );
}

function TargetPicker({ subject, match, value, onChange, disabled }) {
  const t = useAutoTranslation();

  const fitting = match?.candidates || [];
  const others = match?.others || [];
  const label = (c) =>
    `${memberLabel(c.member, t)}${
      c.relation ? ` — ${relationText(c.relation, t)}` : ''
    }`;
  return (
    <FormControl size="small" sx={{ minWidth: 220 }} disabled={disabled}>
      <Select
        displayEmpty
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{
          'aria-label': t('Add {{label}} findings to', {
            label: subjectLabelText(subject.label, t)
          })
        }}
        renderValue={(v) => {
          if (!v) return <em>{t('Don’t add')}</em>;
          const c = [...fitting, ...others].find(
            (x) => memberKey(x.member) === v
          );
          return c ? label(c) : v;
        }}
      >
        <MenuItem value="">
          <em>{t('Don’t add')}</em>
        </MenuItem>
        {fitting.length > 0 && (
          <ListSubheader>
            {t('Fits “{{value}}”', {
              value: subjectLabelText(subject.label, t, { lower: true })
            })}
          </ListSubheader>
        )}
        {fitting.map((c) => (
          <MenuItem key={memberKey(c.member)} value={memberKey(c.member)}>
            {label(c)}
          </MenuItem>
        ))}
        {others.length > 0 && (
          <ListSubheader>{t('Someone else in this family')}</ListSubheader>
        )}
        {others.map((c) => (
          <MenuItem key={memberKey(c.member)} value={memberKey(c.member)}>
            {label(c)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// '“a.pdf”', '“a.pdf” and “b.pdf”', '“a.pdf”, “b.pdf” and “c.pdf”'.
const quotedList = (names, t = translate) => {
  const q = names.map((n) => `“${n}”`);
  return q.length > 1
    ? t('{{q}} and {{value}}', {
        q: q.slice(0, -1).join(', '),
        value: q[q.length - 1]
      })
    : q[0];
};

// With several documents uploaded, the warning names the ones it is about
// (`check.sources`, the files whose own check found a mismatch — TGCLOUD-1850).
function IdentityWarning({ check }) {
  const t = useAutoTranslation();

  if (!check || !(check.dobMismatch || check.surnameMismatch)) return null;
  const sources = [...new Set(check.sources || [])];
  if (sources.length > 1) {
    return (
      <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="identity-warning">
        {t(
          '{{documents}} may be about someone else: the date of birth or name in each does not match this record. Check they are the right documents before adding anything.',
          { documents: quotedList(sources, t) }
        )}
      </Alert>
    );
  }
  const vars = {
    document: sources.length ? quotedList(sources, t) : t('This document'),
    dob: check.documentDob,
    name: check.documentName
  };
  return (
    <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="identity-warning">
      {check.dobMismatch && check.surnameMismatch
        ? t(
            '{{document}} may be about someone else: its date of birth ({{dob}}) and name ({{name}}) do not match this record. Check it is the right document before adding anything.',
            vars
          )
        : check.dobMismatch
        ? t(
            '{{document}} may be about someone else: its date of birth ({{dob}}) does not match this record. Check it is the right document before adding anything.',
            vars
          )
        : t(
            '{{document}} may be about someone else: its name ({{name}}) does not match this record. Check it is the right document before adding anything.',
            vars
          )}
    </Alert>
  );
}

// Why phenotypes and diseases were not looked up. A refusal is not an outage
// (TGCLOUD-1866 D10): a long report used to read as "could not be reached".
// `terminology.sources` names the uploaded files it is about, so a typed note
// analysed afterwards cannot hide which one it was (the review of D10).
const NOT_THE_SAME = 'This is not the same as “nothing found”.';
function terminologyMessage({ reason, sources = [] }, t) {
  const named = [...new Set(sources)];
  const vars = {
    documents: named.length ? quotedList(named, t) : t('This document'),
    notTheSame: t(NOT_THE_SAME)
  };
  if (reason === 'too-long')
    return named.length > 1
      ? t(
          '{{documents}} are too long for the terminology service to read, so phenotypes and diseases in them were not looked up. {{notTheSame}}',
          vars
        )
      : t(
          '{{documents}} is too long for the terminology service to read, so phenotypes and diseases in it were not looked up. {{notTheSame}}',
          vars
        );
  if (reason === 'service-error')
    return named.length
      ? t(
          'The terminology service refused the request for {{documents}}, so phenotypes and diseases were not looked up. {{notTheSame}}',
          vars
        )
      : t(
          'The terminology service refused the request, so phenotypes and diseases were not looked up. {{notTheSame}}',
          vars
        );
  return named.length
    ? t(
        'The terminology service could not be reached for {{documents}}, so phenotypes and diseases were not looked up. {{notTheSame}}',
        vars
      )
    : t(
        'The terminology service could not be reached, so phenotypes and diseases were not looked up. {{notTheSame}}',
        vars
      );
}

function TerminologyNotice({ terminology }) {
  const t = useAutoTranslation();

  if (!terminology || terminology.available !== false) return null;
  const off = terminology.reason === 'system-disabled';
  return (
    <Alert severity={off ? 'info' : 'warning'} sx={{ mb: 1.5 }}>
      {off
        ? t(
            'HPO is switched off in Settings, so no phenotypes or diseases are suggested.'
          )
        : terminologyMessage(terminology, t)}
    </Alert>
  );
}

// An uploaded document whose findings were not read (TGCLOUD-1866 D4): no
// part of it was, or a variant result in it sat outside every part that was
// (the review of D4). Its empty result is "not read", and without this the
// panel showed nothing, exactly as for a negative report.
// `noFindings.sources` names the files.
function NoFindingsNotice({ noFindings }) {
  const t = useAutoTranslation();

  if (!noFindings) return null;
  const sources = [...new Set(noFindings.sources || [])];
  return (
    <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="no-findings-notice">
      {t(
        'Could not find the findings in {{document}} — check it by hand. Part or all of it was not recognised as a result or clinical findings, so an empty list here is not a negative result.',
        {
          document: sources.length ? quotedList(sources, t) : t('this document')
        }
      )}
    </Alert>
  );
}

const FAMILY_NOTE_STATUS = {
  absent: 'not present',
  'not detected': 'not detected',
  // A carrier of a recessive condition is not affected by it (D2).
  carrier: 'carrier, not added as a diagnosis'
};
// The extractor's other statuses, shown as they are named.
const NOTE_STATUSES = new Set([
  'present',
  'past',
  'uncertain',
  'suspected',
  'reported',
  'confirmed',
  'associated'
]);

// 'family' → "Family"; 'maternal aunt:Linda' → "Maternal aunt (Linda)".
function noteSubject(key, t) {
  if (!key || key === 'family') return t('Family');
  const [rel, name] = String(key).split(':');
  const label = relationHeading(rel, t);
  return name ? `${label} (${name})` : label;
}

export default function ClinicalSuggestionsPanel({
  state,
  onAccept,
  disabled = false
}) {
  const t = useAutoTranslation();

  if (!state) return null;
  const {
    analysis,
    analyzingNote,
    matches,
    targetFor,
    setTarget,
    isSelected,
    toggleFinding,
    selectedCount,
    savingNote,
    lastAccept,
    hpoRelease
  } = state;
  const subjects = (analysis?.subjects || []).filter((s) => s.findings.length);
  const notes = analysis?.familyNotes || [];
  const hasAny = subjects.length || notes.length;
  const notice =
    analysis?.terminology?.available === false ||
    analysis?.identityCheck ||
    analysis?.noFindings;
  // The last accept wrote no genotype: the record is about a pregnancy (TGCLOUD-1868, decision 17).
  const genotypeSkipped = lastAccept?.genotypeSkipped === 'prenatal';
  // Genotype suggestions the document gave, not offered on a record about a pregnancy (decision 19):
  // said, even where they were all it gave.
  const genotypesWithheld = analysis?.genotypesWithheld > 0;
  // Genotype suggestions the server did not offer from a report that gives results for more than one
  // person, or for a pregnancy (TGCLOUD-1868 review), whatever record it was uploaded on.
  const reportPeople = analysis?.genotypesNotOffered?.count > 0;
  if (
    disabled ||
    (!hasAny &&
      !analyzingNote &&
      !notice &&
      !lastAccept?.failed?.length &&
      !genotypeSkipped &&
      !genotypesWithheld &&
      !reportPeople)
  )
    return null;

  const hpoDerived = subjects.some((s) =>
    s.findings.some((f) => f.kind !== 'genotype')
  );

  return (
    <Grid size={12}>
      <Box
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          {analyzingNote ? (
            <>
              <CircularProgress size={16} />
              {t('Analyzing…')}
            </>
          ) : (
            t('Suggested Clinical Terms')
          )}
          {!analyzingNote && analysis?.engine && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 400 }}
            >
              {analysis.engine === 'vocabulary+ai'
                ? t(
                    'found by matching the document against HPO, OMIM and Orphanet; those marked AI were added by AI and checked against the document'
                  )
                : t(
                    'found by matching the document against HPO, OMIM and Orphanet'
                  )}
            </Typography>
          )}
        </Typography>

        <IdentityWarning check={analysis?.identityCheck} />
        <NoFindingsNotice noFindings={analysis?.noFindings} />
        <TerminologyNotice terminology={analysis?.terminology} />
        {lastAccept?.failed?.length > 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {lastAccept.failed.length === 1
              ? t(
                  '1 person was not found in this family’s saved pedigree, so nothing was added for them. Save the pedigree and try again.'
                )
              : t(
                  '{{count}} people were not found in this family’s saved pedigree, so nothing was added for them. Save the pedigree and try again.',
                  { count: lastAccept.failed.length }
                )}
          </Alert>
        )}
        {genotypeSkipped && (
          <Alert
            severity="info"
            sx={{ mb: 1.5 }}
            data-testid="suggestions-genotype-skipped"
          >
            {t(PRENATAL_GENOTYPE_SKIPPED_TEXT)}{' '}
            {t('A variant in its document is not this person’s result.')}
          </Alert>
        )}
        {genotypesWithheld && (
          <Alert
            severity="info"
            sx={{ mb: 1.5 }}
            data-testid="suggestions-genotypes-not-offered"
          >
            {t(GENOTYPES_NOT_OFFERED_TEXT)}
          </Alert>
        )}
        {reportPeople && (
          <Alert
            severity="info"
            sx={{ mb: 1.5 }}
            data-testid="suggestions-report-people-genotypes-not-offered"
          >
            {t(REPORT_PEOPLE_GENOTYPES_NOT_OFFERED_TEXT)}
            {analysis.genotypesNotOffered.sources?.length
              ? ` ${t('From {{quotedList}}.', {
                  quotedList: quotedList(
                    [...new Set(analysis.genotypesNotOffered.sources)],
                    t
                  )
                })}`
              : ''}
          </Alert>
        )}

        {subjects.map((s, i) => {
          const target = targetFor(s.key);
          const relative = s.key !== 'self';
          const match = matches?.[s.key];
          const noCandidates =
            relative && !(match?.candidates?.length || match?.others?.length);
          return (
            <Box key={s.key} sx={{ mb: 1.5 }} data-testid={`subject-${s.key}`}>
              {i > 0 && <Divider sx={{ mb: 1 }} />}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  mb: 0.5
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {subjectLabelText(s.label, t)}
                  {s.name ? ` (${s.name})` : ''}
                </Typography>
                {relative && s.relation !== 'partner' && (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {t('add to:')}
                    </Typography>
                    <TargetPicker
                      subject={s}
                      match={match}
                      value={target}
                      onChange={(v) => setTarget(s.key, v)}
                      disabled={savingNote}
                    />
                  </>
                )}
              </Box>
              {noCandidates && (
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ color: 'text.secondary', mb: 0.5 }}
                >
                  {t(
                    'No one saved in this pedigree can take these yet. Add this relative to the pedigree and save it to record them.'
                  )}
                </Typography>
              )}
              {s.relation === 'partner' && (
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ color: 'text.secondary', mb: 0.5 }}
                >
                  {t('Not a blood relative — shown for information only.')}
                </Typography>
              )}
              {s.findings.map((f) => (
                <FindingRow
                  key={`${f.kind}-${f.id}`}
                  subjectKey={s.key}
                  finding={f}
                  checked={isSelected(s.key, f) && !!target}
                  disabled={
                    savingNote ||
                    f.alreadyRecorded ||
                    !target ||
                    s.relation === 'partner'
                  }
                  onToggle={() => toggleFinding(s.key, f)}
                />
              ))}
            </Box>
          );
        })}

        {notes.length > 0 && (
          <Box sx={{ mt: 1 }} data-testid="family-notes">
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {t('Also in the document (not added to anyone)')}
            </Typography>
            {notes.map((n, idx) => (
              <Typography
                key={`${n.name}-${idx}`}
                variant="caption"
                component="div"
                sx={{ color: 'text.secondary' }}
              >
                {noteSubject(n.subject, t)}: {n.name}
                {FAMILY_NOTE_STATUS[n.status]
                  ? ` — ${t(FAMILY_NOTE_STATUS[n.status])}`
                  : n.status
                  ? ` — ${vocabularyText(NOTE_STATUSES, n.status, t)}`
                  : ''}
              </Typography>
            ))}
          </Box>
        )}

        {!analyzingNote && selectedCount > 0 && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              onClick={onAccept}
              disabled={savingNote}
              startIcon={
                savingNote ? <CircularProgress size={16} /> : <AddIcon />
              }
            >
              {t('Add {{selectedCount}} selected', { selectedCount })}
            </Button>
          </Box>
        )}

        {/* HPO licence: the release behind these suggestions. Phenotypes and
            diseases both come from the HPO network; genes do not, so a
            genes-only list carries no HPO line. */}
        {hpoDerived && (
          <HpoReleaseNote release={hpoRelease} sx={{ px: 0, mt: 1 }} />
        )}
      </Box>
    </Grid>
  );
}
